/**
 * 统一 cookie 域名工具
 * 所有写入 cookie 的地方都应使用 getCookieDomain() 获取 domain
 * 确保 cookie 写入到主域（如 .wikibit.com），而非子域（如 www.wikibit.com）
 */

/**
 * 判断是否为本地/开发环境 host
 */
const isLocalHost = (host = '') => {
  const hostname = host.replace(/:\d+$/, '')
  return (
    !hostname ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.startsWith('192.168.')
  )
}

/**
 * 从 host 提取主域，用于 cookie domain
 * 例如：www.wikibit.com -> .wikibit.com
 *       prod.wikibit.com -> .wikibit.com
 *       wikibit.com -> .wikibit.com
 */
export const getCookieDomain = (host?: string): string | undefined => {
  if (!import.meta.client) {
    return undefined
  }

  const runtimeHost = host || window.location.host
  const normalizedHost = runtimeHost.replace(/:\d+$/, '')

  if (isLocalHost(normalizedHost)) {
    return undefined
  }

  const parts = normalizedHost.split('.').filter(Boolean)

  // 处理 .com.cn 等特殊情况
  if (normalizedHost.endsWith('.com.cn') && parts.length >= 3) {
    return `.${parts.slice(-3).join('.')}`
  }

  // 标准情况：取最后两段作为主域
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`
  }

  return undefined
}

/**
 * 删除指定 cookie
 * @param name cookie 名
 * @param domain 可选，指定 domain 则删除 domain cookie，不传则删除当前 host 的 host-only cookie
 */
export const removeCookie = (name: string, domain?: string): void => {
  if (!import.meta.client) {
    return
  }

  const parts = [`${name}=`, 'max-age=0', 'path=/']
  if (domain) parts.push(`domain=${domain}`)
  document.cookie = parts.join('; ')
}

/**
 * 兜底修复：扫描当前 cookie，将非主域的 cookie 迁移到主域
 * 例如：prod.wikibit.com 的 CountryCode -> .wikibit.com 的 CountryCode
 */
export const fixCookieDomain = (): void => {
  if (!import.meta.client) {
    return
  }

  const targetDomain = getCookieDomain()
  if (!targetDomain) {
    return
  }

  const cookies = document.cookie.split(';').map((c) => c.trim())
  const currentHost = window.location.host.replace(/:\d+$/, '')
  const currentDomain = `.${currentHost}`

  // 需要修复的 cookie 列表（与项目中使用的所有 cookie 名保持一致）
  const cookieNames = [
    'CountryCode',
    'CURRIPCOUNTRY',
    'LanguageCode',
    'isWiki',
    'userid',
    'logintoken',
    'quitetoken',
    'refreshtime',
    'userbaseinfo',
    'nickavatar',
    'DJkdikKMG',
    'ViLartidlakd',
    'WsdsfgDGdagYms',
    'wiki-bfp',
    'footer_selected_country_display',
  ]

  cookieNames.forEach((name) => {
    const cookie = cookies.find((c) => c.startsWith(`${name}=`))
    if (!cookie) {
      return
    }

    const value = cookie.substring(name.length + 1)

    // 如果当前 host 不是主域（即存在子域前缀），需要迁移
    if (currentHost !== targetDomain.slice(1)) {
      // 写入到主域
      document.cookie = `${name}=${value}; path=/; domain=${targetDomain}`
      // 删除子域的旧 cookie：同时清理 domain cookie 和 host-only cookie
      removeCookie(name, currentDomain)
      removeCookie(name)
    }
  })
}
